import { CoreButton, CorePaper } from "../../core";
import styles from "./EditQuizModalComponents.module.css";
import i18nPackage from "../../i18n/i18n";
import { CoreModalDescription, CoreModalProps } from "../../core/modal/Modal";
import { useMobileLayout } from "../../hooks";
import { ConditionalWrapper, TooltipWrapper } from "../../wrapper";
import React, { ComponentPropsWithoutRef, forwardRef, useMemo } from "react";
import { CorePaperProps } from "../../core/paper";
import { Skeleton } from "@mui/material";
import { CoreButtonProps } from "../../core/button/Button";
import { StepTitleCard, StepTitleCardProps } from "../edit-quiz-steps/CommonStepComponents";

type EditQuizButtonProps = { wrapInSpan?: boolean } & BaseButtonProps;

const EditQuizButton = forwardRef<HTMLButtonElement, EditQuizButtonProps>(function EditQuizButton(
	{ wrapInSpan = true, hide: _hide, ...props },
	ref,
) {
	return (
		<TooltipWrapper condition={Boolean(props.tooltip)} title={props.tooltip} wrapInSpan={wrapInSpan}>
			<CoreButton {...props} ref={ref}>
				{props.text ?? i18nPackage.t("editQuizPage.components.footer.back")}
			</CoreButton>
		</TooltipWrapper>
	);
});

type BaseButtonProps = {
	disabled?: boolean;
	onClick: () => void | Promise<void>;
	hide?: boolean;
	text?: string;
	tooltip?: React.ReactNode;
	ref?: (node: HTMLButtonElement | null) => void;
} & Omit<CoreButtonProps, "onClick" | "disabled" | "hide" | "children">;

export type EditQuizFooterProps = {
	slotProps: {
		back?: BaseButtonProps;
		saveProfile?: BaseButtonProps;
		confirm?: BaseButtonProps;
		next?: BaseButtonProps;
		cancel?: BaseButtonProps;
	};
	className?: string;
};

export function EditQuizFooter({ slotProps, className }: EditQuizFooterProps) {
	const { back, confirm, next, saveProfile, cancel } = slotProps;

	// UI team wants to bring back cancel button, but refactoring editQuiz and everywhere is another ticket,
	// so leaving these conditionals until we migrate to cancel button everywhere.
	const hasCancelAndBack = Boolean(back) && Boolean(cancel);
	const hasBackAndNotCancel = Boolean(back) && !cancel;

	const backButton = useMemo(() => {
		if (!back || back.hide) return null;
		return (
			<EditQuizButton
				{...back}
				text={back.text ?? i18nPackage.t("editQuizPage.components.footer.back")}
				color={hasCancelAndBack ? "secondary" : "primary"}
			/>
		);
	}, [back, hasCancelAndBack]);

	return (
		<div className={`${styles.editQuizFooter} ${className || ""}`}>
			{hasBackAndNotCancel && backButton}
			{cancel && !cancel.hide && (
				<EditQuizButton
					{...cancel}
					text={cancel.text ?? i18nPackage.t("editQuizPage.components.footer.cancel")}
					color="primary"
				/>
			)}

			<div className={styles.editQuizFooterRightWrapper}>
				{hasCancelAndBack && backButton}
				{saveProfile && !saveProfile.hide && (
					<EditQuizButton
						{...saveProfile}
						wrapInSpan={false}
						tooltip={saveProfile.tooltip ?? i18nPackage.t("editQuizPage.components.footer.saveAsProfileTooltip")}
						text={saveProfile.text ?? i18nPackage.t("editQuizPage.components.footer.saveAsProfile")}
						aria-haspopup="dialog"
					/>
				)}
				{next && !next.hide && (
					<EditQuizButton {...next} text={next.text ?? i18nPackage.t("editQuizPage.components.footer.next")} />
				)}
				{confirm && !confirm.hide && (
					<EditQuizButton {...confirm} text={confirm.text ?? i18nPackage.t("editQuizPage.components.footer.confirm")} />
				)}
			</div>
		</div>
	);
}

export function EditQuizModalSkeleton() {
	return (
		<div className={styles.editQuizSkeletonWrapper}>
			<Skeleton variant="rounded" height={"100%"} width={"25%"} />
			<Skeleton variant="rounded" height={"100%"} width={"75%"} />
		</div>
	);
}

const MODAL_WIDTH_MOBILE: Partial<CoreModalProps> = {
	minWidth: undefined,
	minHeight: undefined,
	widthFixed: true,
};

const MODAL_WIDTH: Partial<CoreModalProps> = {
	minWidth: "min(90%, 980px)",
	minHeight: "min(98%, 710px)",
	widthFixed: true,
};

export function useEditQuizStyles() {
	const getEditQuizModalProps = (isMobileLayout: boolean): Partial<CoreModalProps> => {
		return {
			title: undefined,
			padding: isMobileLayout ? undefined : "1.25rem 0 0 1.25rem",
			dialogContentBackground: isMobileLayout ? "#f4f4f4" : undefined,
			headerSx: {
				borderImage: "none !important",
				borderBottom: "0 !important",
				paddingBottom: "10px",
			},
			fullScreen: isMobileLayout,
			heightFixed: true,
			hideHorizontalOverflow: true,
			hideVerticalOverflow: true,
			contentMatchModalHeight: true,
			ariaLabel: i18nPackage.t("editQuizPage.setupProctoring"),
			...(isMobileLayout ? MODAL_WIDTH_MOBILE : MODAL_WIDTH),
		};
	};

	const getEditQuizModalWidth = (isMobileLayout: boolean): Partial<CoreModalProps> => {
		return isMobileLayout ? MODAL_WIDTH_MOBILE : MODAL_WIDTH;
	};

	/**styling for modal in modals */
	const getModalInModalProps = (isMobileLayout: boolean, title?: React.ReactNode): Partial<CoreModalProps> => {
		return {
			title: isMobileLayout ? undefined : title,
			heightFixed: isMobileLayout,
			fullScreen: isMobileLayout,
			showTopCloseButton: !isMobileLayout,
			dialogContentBackground: isMobileLayout ? "#f4f4f4" : undefined,
		};
	};

	return { getEditQuizModalProps, getEditQuizModalWidth, getModalInModalProps };
}

type ResponsiveModalHeaderProps = {
	isMobileLayout: boolean;
	titleCardProps?: Omit<StepTitleCardProps, "title" | "description">;
	/**Will only show on mobile */
	title?: React.ReactNode;
	description?: React.ReactNode;
};

function ResponsiveModalHeader({ isMobileLayout, description, titleCardProps, title }: ResponsiveModalHeaderProps) {
	return isMobileLayout ? (
		<StepTitleCard title={title} description={description} {...titleCardProps} />
	) : (
		<CoreModalDescription>{description}</CoreModalDescription>
	);
}

type ResponsiveModalContentProps = {
	isMobileLayout: boolean;
	paperProps?: Omit<CorePaperProps, "children">;
	rootProps?: Omit<ComponentPropsWithoutRef<"div">, "children">;
	/**Goes on CorePaper on mobile and wrapper <div> when not mobile. This wraps the children */
	contentClassName?: string;
	children: React.ReactNode;
};

function ResponsiveModalContent({
	isMobileLayout,
	rootProps,
	contentClassName,
	paperProps,
	children,
}: ResponsiveModalContentProps) {
	return (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => (
				<CorePaper
					{...paperProps}
					sx={{ padding: "0.5rem", ...paperProps?.sx }}
					component="section"
					className={`${contentClassName || ""} ${paperProps?.className || ""}`}
				>
					{children}
				</CorePaper>
			)}
			whenFalse={(children) => (
				<div {...rootProps} className={`${contentClassName || ""} ${rootProps?.className || ""}`}>
					{children}
				</div>
			)}
		>
			{children}
		</ConditionalWrapper>
	);
}

type ResponsiveModalBodyProps = {
	slotProps?: {
		modalContainer?: Omit<ComponentPropsWithoutRef<"div">, "children">;
		titleCard?: ResponsiveModalHeaderProps["titleCardProps"];
		paper?: ResponsiveModalContentProps["paperProps"];
		contentRoot?: ResponsiveModalContentProps["rootProps"];
	};
	children: React.ReactNode;
} & Pick<ResponsiveModalHeaderProps, "description" | "title"> &
	Pick<ResponsiveModalContentProps, "contentClassName">;

/**Handles mobile layout for modals in modals */
export function ResponsiveModalBody({
	slotProps,
	description,
	title,
	children,
	contentClassName,
}: ResponsiveModalBodyProps) {
	const isMobileLayout = useMobileLayout();

	return (
		<div
			{...slotProps?.modalContainer}
			className={`${styles.modalContainer} ${slotProps?.modalContainer?.className || ""}`}
		>
			<ResponsiveModalHeader
				{...slotProps?.titleCard}
				isMobileLayout={isMobileLayout}
				title={title}
				description={description}
			/>

			<ResponsiveModalContent
				isMobileLayout={isMobileLayout}
				contentClassName={contentClassName}
				rootProps={slotProps?.contentRoot}
				paperProps={slotProps?.paper}
			>
				{children}
			</ResponsiveModalContent>
		</div>
	);
}
